<template>
    <div class="w-full h-full">
        <transition
            enter-active-class="transition duration-400 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-100 ease-out"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="h-full"
        >
            <div v-if="isLoading" class="fixed top-0 bottom-0 left-0 right-0 bg-black/[0.7] flex flex-col justify-center items-center z-40"
                    tabindex="-1" >
                <div class="flex flex-col justify-center items-center p-4 rounded-md">
                <svg aria-hidden="true" class="mx-1 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#d3d3d3"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="#2E9CCA"/>
                </svg>
                <div class="text-white p-4 whitespace-nowrap text-sm">
                    Please wait…
                </div>
                </div>
            </div>
        </transition>
        <div class="pt-1 w-full mb-2">
            <BaseTabBar
                :tabList="tabList"
                :selectedColor="selectedColor"
                :notSelectedColor="notSelectedColor"
                :hoverColor="hoverColor"
                @set-page="setView"
            />
        </div>
        <div v-if="currentTab === 'edit'" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <InventoryItemForm
                :inventory-item="currentItem"
                :user-options="userOptions"
                :inventory-category-options="inventoryCategoryOptions"
                @submit-form="editInventoryItem"
                @cancel="closeEditModal"
            />
        </div>
        <div v-if="currentTab === 'assignment'" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <InventoryAssignmentHistory
                :inventory-item="currentItem"
                :user-options="userOptions"
                @refetch="refetchItemAndBubble"
            />
        </div>
        <div v-if="currentTab === 'issues'" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <InventoryIssuesTab
                :inventory-item="currentItem"
                @refetch="refetchItemAndBubble"
            />
        </div>
        <div v-if="currentTab === 'files'" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <InventoryFileAttachments
                :inventory-item="currentItem"
                @submit-form="editInventoryItemFiles"
                @cancel="closeEditModal"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue'
import BaseTabBar from '../BaseTabBar.vue';
import InventoryItemForm from './InventoryItemForm.vue';
import InventoryAssignmentHistory from './InventoryAssignmentHistory.vue';
import InventoryIssuesTab from './InventoryIssuesTab.vue';
import InventoryFileAttachments from './InventoryFileAttachments.vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { updateInventoryItemAndOwnership, updateInventoryItem, getInventoryItems } from '@/services/Inventory';

const props = defineProps([
    "inventoryItem", "userOptions", "inventoryCategoryOptions", "initialTab",
])

const store = useStore()
const $toast = useToast()
const emit = defineEmits(["submit-edit-form","close-edit-form", "submit-add-form", "close-add-form", "refetch-inventory-items"]);

// Track an internal copy so we can refresh derived fields after issue/return
// without forcing the parent grid to refetch on every action.
const currentItem = ref(props.inventoryItem);

function fetchNewInventoryItems(){
    emit('refetch-inventory-items')
}

async function refetchItemAndBubble() {
    // Re-fetch the single item so derived fields (lifecycle, photos, etc.)
    // update without leaving the modal. Also bubble up so the grid refreshes.
    try {
        const token = store.state.auth.token;
        const resp = await getInventoryItems(token, `?id=${currentItem.value.id}`);
        const list = resp?.data?.results || resp?.data || [];
        const fresh = list.find(i => i.id === currentItem.value.id);
        if (fresh) currentItem.value = fresh;
    } catch (e) {
        // Non-fatal — the grid refresh below will catch up.
    }
    fetchNewInventoryItems();
}

function closeEditModal(){
    emit('close-edit-form')
}

async function editInventoryItem(formData, close){
    const token = store.state.auth.token
    const result = await updateInventoryItemAndOwnership(currentItem.value.id, formData, token)
    if(result.status == 200){
        if (result.data && result.data.id) {
            currentItem.value = result.data;
        }
        fetchNewInventoryItems()
        $toast.open({
            message: `Successfully updated inventory item`,
            type: 'success',
            duration: 3000
        })
        if(close){
            closeEditModal()
        }
    } else {
        const msg = result?.response?.data
            ? (typeof result.response.data === 'string' ? result.response.data : JSON.stringify(result.response.data))
            : 'Error, could not update inventory item';
        $toast.open({ message: msg, type: 'error', duration: 5000 });
    }
}

async function editInventoryItemFiles(formData){
    const token = store.state.auth.token
    const result = await updateInventoryItem(currentItem.value.id, formData, token)
    if(result.status == 200){
        fetchNewInventoryItems()
        $toast.open({
            message: `Successfully updated inventory item`,
            type: 'success',
            duration: 3000
        })
    } else {
        $toast.open({
            message: `Error, could not update inventory item`,
            type: 'error',
            duration: 3000
        })
    }
}


const isLoading = ref(false)

const initialTab = props.initialTab || 'edit';
const tabList = ref([
    {tabName: 'Edit Inventory Item', isSelected: initialTab === 'edit'},
    {tabName: 'Assignment History', isSelected: initialTab === 'assignment'},
    {tabName: 'Issues', isSelected: initialTab === 'issues'},
    {tabName: 'File Attachments', isSelected: initialTab === 'files'},
])
const selectedColor = 'white';
const notSelectedColor = 'gray-100'
const hoverColor = 'slate-50'

const currentTab = ref(initialTab);

function setView(tabName) {
    const map = {
        'Edit Inventory Item': 'edit',
        'Assignment History': 'assignment',
        'Issues': 'issues',
        'File Attachments': 'files',
    };
    const next = map[tabName] || 'edit';
    currentTab.value = next;
    tabList.value.forEach(t => { t.isSelected = (t.tabName === tabName); });
}
</script>
